// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { classNames } from '@shared/ui-kit/utils';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { useProgressTracker } from './use-progress-tracker';

const renderBlockLabel = (label: string | [string, { ns: string }]) => (
  <Translation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    i18nKey={(Array.isArray(label) ? label[0] : label) as any}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ns={(Array.isArray(label) ? label[1].ns : 'components') as any}
  />
);

export const ProgressTracker = defineComponent({
  displayName: 'ProgressTracker',
  useSetup: useProgressTracker,
  View: ({
    blocks,
    displayWorkflow,
    showPhaseOverline,
    flowPhaseNumber,
    flowPhaseTotal,
    stepCurrent,
    stepTotal,
    progress,
    currentBlock,
  }) => (
    <div className="hidden w-[320px] shrink-0 flex-col gap-y-6 overflow-y-auto border-r bg-section p-6 lg:flex">
      <header className="flex flex-col gap-1">
        {showPhaseOverline && (
          <Typography
            variant="caption"
            className="!font-medium !text-subtitle"
            data-testid="flow-phase-overline"
          >
            <Translation
              i18nKey="combined-flow.progress.phase"
              ns="components"
              values={{ phase: flowPhaseNumber, phaseTotal: flowPhaseTotal }}
            />
          </Typography>
        )}
        <Typography variant="h2" className="min-w-0" data-testid="title">
          {displayWorkflow === 'account-opening' ? (
            <Translation i18nKey="account-opening.header.title" ns="components" />
          ) : (
            <Translation
              i18nKey="onboarding-progress-tracker.title"
              ns="components"
            />
          )}
        </Typography>
      </header>

      <section className="flex flex-col gap-y-2" data-testid="progress-container">
        <Typography variant="body2" data-testid="progress-label">
          <Translation
            i18nKey="onboarding-progress-tracker.step"
            ns="components"
            values={{ current: stepCurrent, total: stepTotal }}
          />
        </Typography>

        <LinearProgress
          color="secondary"
          variant="determinate"
          value={progress}
          className="!h-2 !rounded-full"
          data-testid="progress-bar"
        />
      </section>

      <section>
        <List dense className="!space-y-1">
          {Array.from(blocks, (block, index) => (
            <ListItem
              key={block.id}
              data-testid="block-item"
              sx={{ alignItems: 'center' }}
            >
              <ListItemIcon sx={{ minWidth: 32, alignSelf: 'center' }}>
                <span
                  className={classNames(
                    'flex h-7 w-7 items-center justify-center rounded-full bg-border-medium text-body font-semibold',
                    'aria-checked:bg-positive aria-checked:text-white',
                    'aria-[current=true]:bg-secondary aria-[current=true]:text-white',
                  )}
                  aria-current={currentBlock?.id === block.id}
                  aria-checked={block.completed}
                  data-testid="block-item-index"
                >
                  {index + 1}
                </span>
              </ListItemIcon>

              <ListItemText
                primary={renderBlockLabel(block.label)}
                className={classNames({
                  '!font-semibold !text-positive':
                    block.completed && currentBlock?.id !== block.id,
                  '!font-semibold !text-secondary': currentBlock?.id === block.id,
                  '!line-through': block.completed && currentBlock?.id !== block.id,
                  '!text-subtitle':
                    !block.completed && currentBlock?.id !== block.id,
                })}
                data-testid="block-item-label"
                primaryTypographyProps={{
                  sx: {
                    color: 'inherit',
                    fontWeight: 'inherit',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </section>
    </div>
  ),
});
